/**
 * Strich-Symbole für die Karten der Lernstrecke.
 *
 * Jede Karte trägt vorne ihr eigenes Zeichen — der Begriff ist damit schon
 * erkannt, bevor die Überschrift gelesen ist, und zehn Karten untereinander
 * sind nicht mehr zehn gleich aussehende Kästen.
 *
 * **Warum hier und nicht im Kapiteltext:** In `chapters.js` stünden zwischen
 * zwei Sätzen fünf Zeilen Pfadangaben; der Text wäre nicht mehr zu lesen. Hier
 * liegen sie beisammen, jede genau einmal, und im Kapitel steht nur
 * `${ico('warenkorb')}`.
 *
 * **Warum inline und nicht als Maske wie die Kastensymbole** (`.note .ico` in
 * `style.module.scss`): Die sieben Kastensymbole wiederholen sich in fünfzehn
 * Kästen — dort spart die Maske jede Wiederholung. Diese hier sind je Karte
 * ein eigenes Bild, und als `<svg>` bleibt die Zeichnung dort stehen, wo sie
 * hingehört.
 *
 * Gezeichnet wird nur die Linie: Farbe, Strichstärke und Größe stehen in
 * `style.module.scss` bei `.kico`, damit alle Symbole gleich aussehen und die
 * Farbe des Kastens erben.
 */

const ZEICHNUNGEN = {
	// ---- Kapitel 1: die zehn Begriffe ----
	webshop: `<path d="M5.4 3.6h13.2l2 5.8H3.4l2-5.8z"/><path d="M3 9.4h18"/>
		<path d="M4.8 9.4V19a1.4 1.4 0 0 0 1.4 1.4h11.6a1.4 1.4 0 0 0 1.4-1.4V9.4"/>
		<path d="M9.8 20.4v-5.2h4.4v5.2"/>`,
	warenkorb: `<circle cx="9.4" cy="19.6" r="1.3"/><circle cx="17.6" cy="19.6" r="1.3"/>
		<path d="M2.6 3.4h2.6l2.2 11.2h11l1.9-7.8H6.1"/>`,
	conversion: `<path d="M5.4 3.8 12 19.4l2-5.4 5.4-2L5.4 3.8z"/><path d="m14.8 18.4 1.9 1.9 3.7-4.1"/>`,
	prozent: `<circle cx="8.2" cy="8.2" r="2.4"/><circle cx="15.8" cy="15.8" r="2.4"/><path d="M17.6 6.4 6.4 17.6"/>`,
	muenzen: `<ellipse cx="12" cy="6.6" rx="7.4" ry="3"/>
		<path d="M4.6 6.6v4.4c0 1.66 3.31 3 7.4 3s7.4-1.34 7.4-3V6.6"/>
		<path d="M4.6 11v4.4c0 1.66 3.31 3 7.4 3s7.4-1.34 7.4-3V11"/>`,
	aufwaerts: `<path d="M3.4 16.6 9.2 10.8l3.4 3.4L20.6 6.2"/><path d="M15.4 6.2h5.2v5.2"/>`,
	dazu: `<rect x="2.4" y="8.6" width="6.6" height="6.8" rx="1.8"/>
		<rect x="15" y="8.6" width="6.6" height="6.8" rx="1.8"/>
		<path d="M12 10.4v3.2"/><path d="M10.4 12h3.2"/>`,
	abbruch: `<path d="M5.6 7.8h12.8l-1.1 11.3a1.6 1.6 0 0 1-1.6 1.5H8.3a1.6 1.6 0 0 1-1.6-1.5L5.6 7.8z"/>
		<path d="M9 10V6.8a3 3 0 0 1 6 0V10"/><path d="m10.3 13.6 3.4 3.4"/><path d="m13.7 13.6-3.4 3.4"/>`,
	kassa: `<rect x="2.6" y="5" width="18.8" height="14" rx="2.4"/><path d="M2.6 9.6h18.8"/>
		<path d="m8.8 14.6 2 2 4.4-4.6"/>`,
	zurueck: `<path d="M20.4 12a8.4 8.4 0 1 1-8.4-8.4c2.35 0 4.6.99 6.22 2.63L20.4 8.4"/>
		<path d="M20.4 3.6v4.8h-4.8"/><circle cx="12" cy="10.6" r="2.1"/>
		<path d="M8.4 16.4a3.9 3.9 0 0 1 7.2 0"/>`,

	// ---- Kapitel 6: Technik, die man merkt ----
	stoppuhr: `<circle cx="12" cy="13.6" r="7.6"/><path d="M12 9.8v3.8l2.4 1.6"/>
		<path d="M9.6 2.8h4.8"/><path d="M12 2.8v3.2"/>`,
	handy: `<rect x="6.4" y="2.4" width="11.2" height="19.2" rx="2.6"/><path d="M10.4 5.4h3.2"/>
		<path d="M12 18.4v.4"/>`,
	schloss: `<rect x="4.2" y="10.2" width="15.6" height="10.6" rx="2.4"/>
		<path d="M8 10.2V7.4a4 4 0 0 1 8 0v2.8"/><path d="M12 14.4v2.2"/>`,
	schild: `<path d="M12 2.8 4.8 5.8v6.1c0 4.4 3.05 7.9 7.2 9.3 4.15-1.4 7.2-4.9 7.2-9.3V5.8L12 2.8z"/>
		<path d="m8.9 12.2 2.2 2.2 4-4.4"/>`,

	// ---- Kapitel 7: die drei Servicekanäle ----
	telefon: `<path d="M21.4 16.9v2.9a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 1.52 4.1 2 2 0 0 1 3.51 1.9h2.9a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L7.59 9.61a16 16 0 0 0 6 6l1.07-1.07a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7a2 2 0 0 1 1.72 2.02z"/>`,
	brief: `<rect x="2.6" y="4.8" width="18.8" height="14.4" rx="2.4"/><path d="m3.4 6.4 8.6 6.2 8.6-6.2"/>`,
	sprechblase: `<path d="M20.6 12.2a7.8 7.8 0 0 1-8.4 7.8c-.94 0-1.85-.16-2.7-.45L4.2 21.2l1.6-5.2a7.8 7.8 0 1 1 14.8-3.8z"/>
		<path d="M8.9 12.4h.02"/><path d="M12.4 12.4h.02"/><path d="M15.9 12.4h.02"/>`,

	// ---- Kapitel 2: die fünf Zeilen einer Persona ----
	person: `<circle cx="12" cy="8.4" r="3.6"/><path d="M4.8 20.4a7.2 7.2 0 0 1 14.4 0"/>`,
	herz: `<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7 7-7z"/>`,
	funk: `<circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.48"/>
		<path d="M7.76 16.24a6 6 0 0 1 0-8.48"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
		<path d="M4.93 19.07a10 10 0 0 1 0-14.14"/>`,
	achtung: `<circle cx="12" cy="12" r="8.6"/><path d="M12 7.6v5"/><path d="M12 16.1v.3"/>`
}

/**
 * Das Symbol als fertiges Markup für den Kapiteltext.
 *
 * Ein unbekannter Name bricht sofort — die Kapitel werden beim Laden des
 * Moduls zusammengesetzt, ein Tippfehler fiele also beim ersten Aufruf der
 * Seite und in den Tests auf und nicht erst dort, wo eine Karte ohne Zeichen
 * dasteht.
 */
export const ico = (name, klasse = '') => {
	if (!ZEICHNUNGEN[name]) throw new Error(`Unbekanntes Kartensymbol: ${name}`)

	return `<span class="kico ${klasse}" aria-hidden="true"><svg viewBox="0 0 24 24">${ZEICHNUNGEN[name]}</svg></span>`
}
